import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, ChevronDown, ChevronUp, Image as ImageIcon, ShoppingBag, Store, Smartphone, AlertCircle, Loader2, Truck } from "lucide-react";

const STATUSES = ["paid", "processing", "ready_for_pickup", "out_for_delivery", "completed", "cancelled"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function StatusBadge({ status, payment_status }) {
  const map = {
    awaiting_payment: ["bg-amber-100 text-amber-800", "Awaiting payment"],
    paid:             ["bg-green-100 text-green-800", "Paid"],
    processing:       ["bg-blue-100 text-blue-800", "Processing"],
    ready_for_pickup: ["bg-emerald-100 text-emerald-800", "Ready for pickup"],
    out_for_delivery: ["bg-sky-100 text-sky-800", "Out for delivery"],
    completed:        ["bg-gray-200 text-gray-800", "Completed"],
    cancelled:        ["bg-red-100 text-red-700", "Cancelled"],
    on_hold:          ["bg-blue-100 text-blue-800", "On hold · deposit"],
    pending:          ["bg-amber-100 text-amber-800", "Pending"],
  };
  const [cls, label] = map[status] || ["bg-gray-100 text-gray-700", status];
  return <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cls}`}>{label}</span>;
}

function ChannelBadge({ channel, payment_method }) {
  if (channel === "pos") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] bg-[#0A1C40] text-white px-2 py-0.5 font-bold tracking-wider">
        <Store className="w-3 h-3" /> POS · {payment_method === "cash" ? "CASH" : "CARD"}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] bg-blue-50 text-blue-800 px-2 py-0.5 font-bold tracking-wider">
      <Smartphone className="w-3 h-3" /> WEB
    </span>
  );
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | pending | paid
  const [reschedDate, setReschedDate] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/orders/admin/all");
      setOrders(data.items || []);
    } catch (e) {
      toast.error("Couldn't load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/orders/admin/all");
        if (!cancelled) setOrders(data.items || []);
      } catch {
        if (!cancelled) toast.error("Couldn't load orders");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/orders/admin/${id}/status?status=${status}`);
      toast.success("Status updated");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const cancelOrder = async (o) => {
    const isUnpaid = o.payment_status === "pending" || o.status === "awaiting_payment";
    const confirmMsg = isUnpaid
      ? `Cancel ${o.order_number}? This was never paid — nothing was charged. Stock will be returned to inventory.`
      : `Cancel ${o.order_number}? You may need to issue a refund through Square separately.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.patch(`/orders/admin/${o.id}/status?status=cancelled`);
      toast.success(`Order ${o.order_number} cancelled`);
      load();
    } catch {
      toast.error("Cancel failed");
    }
  };

  const rescheduleDelivery = async (o) => {
    if (!reschedDate) return;
    try {
      await api.patch(`/orders/admin/${o.id}/delivery-date?date=${reschedDate}`);
      toast.success("Delivery scheduled — customer notified by email/SMS");
      setReschedDate("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const filtered = useMemo(() => {
    if (filter === "all") return orders;
    if (filter === "pending") return orders.filter(o => o.payment_status === "pending" || o.status === "awaiting_payment");
    if (filter === "paid") return orders.filter(o => o.payment_status === "paid");
    if (filter === "active") return orders.filter(o => ["processing","ready_for_pickup","out_for_delivery"].includes(o.status));
    return orders;
  }, [orders, filter]);

  const pendingCount = orders.filter(o => o.payment_status === "pending" || o.status === "awaiting_payment").length;

  return (
    <div data-testid="admin-orders">
      <div className="ipv-eyebrow text-[#0A1C40]">Operations</div>
      <h1 className="font-display font-black text-3xl tracking-tighter mt-2">Orders</h1>

      {/* Filter tabs */}
      <div className="flex gap-1 border-b border-gray-200 mt-6">
        {[
          { id: "all",     label: `All (${orders.length})` },
          { id: "pending", label: `Unpaid${pendingCount ? ` (${pendingCount})` : ""}` },
          { id: "paid",    label: "Paid" },
          { id: "active",  label: "In-progress" },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setFilter(t.id)}
            className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px ${
              filter === t.id ? "border-[#0A1C40] text-[#0A1C40]" : "border-transparent text-gray-500 hover:text-black"
            }`}
            data-testid={`orders-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="text-sm text-gray-500 mt-6 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading orders…
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="border border-dashed border-gray-300 mt-8 p-10 text-center text-gray-500" data-testid="admin-orders-empty">
          No orders match this filter.
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="bg-white border border-gray-200 mt-6 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] border-b border-gray-200 ipv-eyebrow text-left">
              <tr>
                <th className="p-3 w-8"></th>
                <th className="p-3">Order</th>
                <th className="p-3">Customer / Channel</th>
                <th className="p-3">Fulfillment</th>
                <th className="p-3 text-right">Total</th>
                <th className="p-3">Status</th>
                <th className="p-3">Date</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((o) => {
                const isUnpaid = o.payment_status === "pending" || o.status === "awaiting_payment";
                const open = expanded === o.id;
                return (
                  <React.Fragment key={o.id}>
                    <tr
                      className={`hover:bg-gray-50 cursor-pointer ${isUnpaid ? "bg-amber-50/30" : ""}`}
                      onClick={() => setExpanded(open ? null : o.id)}
                      data-testid={`admin-order-${o.order_number}`}
                    >
                      <td className="p-3">{open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}</td>
                      <td className="p-3 font-bold">
                        {o.order_number}
                        {isUnpaid && <AlertCircle className="w-3 h-3 inline ml-1 text-amber-600" />}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <span>{o.customer_name || "—"}</span>
                          <ChannelBadge channel={o.channel} payment_method={o.payment_method} />
                        </div>
                        {o.customer_email && <div className="text-xs text-gray-500">{o.customer_email}</div>}
                        {o.served_by_name && <div className="text-xs text-gray-400">served by {o.served_by_name}</div>}
                      </td>
                      <td className="p-3 capitalize">
                        {o.fulfillment}
                        {o.delivery_date && (
                          <div className="text-[10px] text-[#0A1C40] font-bold normal-case" data-testid={`row-delivery-date-${o.order_number}`}>
                            🚚 {o.delivery_date}
                          </div>
                        )}
                        {o.fulfillment === "pickup" && o.pickup_date && (
                          <div className="text-[10px] text-[#0A1C40] font-bold normal-case">🗓 {o.pickup_date}</div>
                        )}
                      </td>
                      <td className="p-3 text-right font-bold">{fmtMoney(o.total)}</td>
                      <td className="p-3">
                        {isUnpaid || o.status === "on_hold" ? (
                          <StatusBadge status={o.status} />
                        ) : (
                          <select
                            value={o.status}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => updateStatus(o.id, e.target.value)}
                            className="border border-gray-200 px-2 py-1 text-xs"
                            data-testid={`status-select-${o.order_number}`}
                          >
                            {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                          </select>
                        )}
                      </td>
                      <td className="p-3 text-gray-500 text-xs">
                        {new Date(o.created_at).toLocaleDateString()}
                        <div>{new Date(o.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                      </td>
                      <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                        {(isUnpaid || o.status === "cancelled") ? (
                          <button
                            onClick={() => cancelOrder(o)}
                            className="text-xs text-red-600 hover:bg-red-50 px-2 py-1 inline-flex items-center gap-1"
                            data-testid={`order-cancel-${o.order_number}`}
                            disabled={o.status === "cancelled"}
                          >
                            <Trash2 className="w-3 h-3" /> {o.status === "cancelled" ? "Cancelled" : "Cancel"}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={8} className="p-5 bg-[#FAFAFA]">
                          {isUnpaid && (
                            <div className="mb-4 border-l-4 border-amber-500 bg-amber-50 p-3 text-sm" data-testid={`unpaid-banner-${o.order_number}`}>
                              <b className="text-amber-800">This order was never paid.</b>{" "}
                              No money was charged. The customer abandoned checkout, or — if it was a POS sale — the Square Terminal payment never completed.
                              Stock has NOT been decremented. You can safely cancel it.
                            </div>
                          )}
                          <div className="grid md:grid-cols-2 gap-6 text-sm">
                            <div>
                              <div className="ipv-eyebrow mb-3 flex items-center gap-1"><ShoppingBag className="w-3 h-3" /> Items</div>
                              <div className="space-y-3">
                                {(o.line_items || []).map((li, idx) => (
                                  <div key={`${li.product_id}-${idx}`} className="flex items-start gap-3 bg-white border border-gray-200 p-2">
                                    <div className="w-16 h-16 bg-gray-100 flex-shrink-0 overflow-hidden">
                                      {li.image ? (
                                        <img
                                          src={`${BACKEND}/api/files/${li.image}`}
                                          alt={li.name}
                                          className="w-full h-full object-cover"
                                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                                          data-testid={`order-item-image-${idx}`}
                                        />
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                                          <ImageIcon className="w-6 h-6" />
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-bold">{li.name}</div>
                                      <div className="text-xs text-gray-500">
                                        {li.sku && <span>SKU {li.sku} · </span>}
                                        Qty {li.quantity}
                                        {li.color && <span> · {li.color}</span>}
                                      </div>
                                      <div className="text-xs text-gray-500">{fmtMoney(li.unit_price)} each</div>
                                    </div>
                                    <div className="font-bold whitespace-nowrap">{fmtMoney(li.line_total)}</div>
                                  </div>
                                ))}
                              </div>
                              <div className="mt-4 space-y-1 text-xs text-gray-700 pt-3 border-t border-gray-200">
                                <div className="flex justify-between"><span>Subtotal</span><span>{fmtMoney(o.subtotal)}</span></div>
                                {o.deal_savings > 0 && (
                                  <div className="flex justify-between text-green-700"><span>Deal savings</span><span>−{fmtMoney(o.deal_savings)}</span></div>
                                )}
                                {o.member_discount_amount > 0 && (
                                  <div className="flex justify-between text-green-700"><span>Member 10%</span><span>−{fmtMoney(o.member_discount_amount)}</span></div>
                                )}
                                {o.delivery_fee > 0 && (
                                  <div className="flex justify-between"><span>Delivery</span><span>{fmtMoney(o.delivery_fee)}</span></div>
                                )}
                                <div className="flex justify-between"><span>Tax</span><span>{fmtMoney(o.tax)}</span></div>
                                <div className="flex justify-between font-bold text-base pt-1 border-t border-gray-200"><span>Total</span><span>{fmtMoney(o.total)}</span></div>
                              </div>
                            </div>
                            <div>
                              <div className="ipv-eyebrow mb-3 flex items-center gap-1"><Truck className="w-3 h-3" /> Fulfillment</div>
                              <div className="space-y-1 text-gray-700">
                                <div><b>Method:</b> <span className="capitalize">{o.fulfillment}</span></div>
                                {o.fulfillment === "delivery" && o.delivery_address && (
                                  <div>
                                    <b>Address:</b> {o.delivery_address}, {o.delivery_city}, {o.delivery_state} {o.delivery_zip}
                                  </div>
                                )}
                                {o.status === "on_hold" && (
                                  <div className="mt-2 bg-blue-50 border-2 border-[#0A1C40] p-3" data-testid={`hold-box-${o.order_number}`}>
                                    <div className="font-bold text-[#0A1C40]">
                                      ON HOLD — $50 deposit paid · Balance due: {fmtMoney(o.balance_due || 0)}
                                      {o.hold_expires_at && <span className="font-normal text-gray-600"> · expires {new Date(o.hold_expires_at).toLocaleDateString()}</span>}
                                    </div>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      <button
                                        onClick={async () => {
                                          try {
                                            await api.post(`/orders/admin/${o.id}/collect-balance`);
                                            toast.success("Balance payment link sent to the customer");
                                          } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                                        }}
                                        className="text-xs bg-[#0A1C40] text-white px-3 py-1.5 font-bold"
                                        data-testid={`send-balance-link-${o.order_number}`}
                                      >
                                        Send balance payment link
                                      </button>
                                      <button
                                        onClick={async () => {
                                          try {
                                            await api.post(`/orders/admin/${o.id}/balance-paid`);
                                            toast.success("Marked paid — order moves to Paid");
                                            load();
                                          } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                                        }}
                                        className="text-xs border-2 border-[#0A1C40] text-[#0A1C40] px-3 py-1.5 font-bold"
                                        data-testid={`balance-paid-${o.order_number}`}
                                      >
                                        Balance paid in store
                                      </button>
                                    </div>
                                  </div>
                                )}
                                {o.delivery_exception && (
                                  <div className="mt-2 border-l-4 border-amber-500 bg-amber-50 p-2 text-xs" data-testid={`exception-flag-${o.order_number}`}>
                                    ⚠ <b>{o.delivery_exception.reason === "not_home" ? "Customer not home" : "Customer refused delivery"}</b>
                                    {" "}— flagged by {o.delivery_exception.by} · {new Date(o.delivery_exception.at).toLocaleString()}. Reschedule below.
                                  </div>
                                )}
                                {o.delivered_at && (
                                  <div className="mt-2 text-xs text-green-700">
                                    ✓ Delivered {new Date(o.delivered_at).toLocaleString()} by {o.delivered_by || "driver"} ·{" "}
                                    {o.delivery_no_damage ? "no damage confirmed" : "damage photo on file"}
                                    {o.delivery_signature_path && (
                                      <a className="underline ml-1" href={`${BACKEND}/api/files/${o.delivery_signature_path}`} target="_blank" rel="noreferrer">signature</a>
                                    )}
                                    {o.delivery_damage_photo_path && (
                                      <a className="underline ml-1" href={`${BACKEND}/api/files/${o.delivery_damage_photo_path}`} target="_blank" rel="noreferrer">photo</a>
                                    )}
                                  </div>
                                )}
                                {o.fulfillment === "delivery" && (
                                  <div className="mt-2 bg-white border border-gray-200 p-3" data-testid={`delivery-schedule-${o.order_number}`}>
                                    <div>
                                      <b>Scheduled delivery:</b>{" "}
                                      {o.delivery_date ? (
                                        <span className="text-[#0A1C40] font-bold">
                                          {o.delivery_date}
                                          {o.delivery_window ? ` · ${o.delivery_window}` : ""}
                                        </span>
                                      ) : (
                                        <span className="text-amber-700">{o.rush ? "RUSH — arrange by phone" : "Not scheduled"}</span>
                                      )}
                                    </div>
                                    {(o.status === "on_hold" || o.status === "awaiting_payment" || o.payment_status === "pending") ? (
                                      <div className="mt-2 text-xs bg-amber-50 border border-amber-400 text-amber-800 px-2.5 py-2 font-semibold" data-testid={`schedule-blocked-${o.order_number}`}>
                                        ⚠ Delivery can't be scheduled until this order is paid in full.
                                        {o.status === "on_hold" && " Collect the balance first (buttons above)."}
                                      </div>
                                    ) : (
                                    <div className="flex gap-2 mt-2 items-center">
                                      <input
                                        type="date"
                                        className="border border-gray-200 px-2 py-1 text-xs"
                                        value={reschedDate}
                                        onChange={(e) => setReschedDate(e.target.value)}
                                        data-testid={`resched-input-${o.order_number}`}
                                      />
                                      <button
                                        onClick={() => rescheduleDelivery(o)}
                                        disabled={!reschedDate}
                                        className="text-xs bg-[#0A1C40] text-white px-3 py-1.5 font-bold disabled:opacity-40"
                                        data-testid={`resched-btn-${o.order_number}`}
                                      >
                                        {o.delivery_date ? "Reschedule + notify" : "Schedule + notify"}
                                      </button>
                                    </div>
                                    )}
                                  </div>
                                )}
                                {o.fulfillment === "pickup" && o.pickup_date && (
                                  <div><b>Preferred pickup day:</b> {o.pickup_date}</div>
                                )}
                                {o.customer_phone && <div><b>Phone:</b> <a href={`tel:${o.customer_phone}`} className="text-[#0A1C40] hover:underline">{o.customer_phone}</a></div>}
                                {o.customer_email && <div><b>Email:</b> <a href={`mailto:${o.customer_email}`} className="text-[#0A1C40] hover:underline">{o.customer_email}</a></div>}
                                {o.note && <div><b>Notes:</b> {o.note}</div>}
                              </div>

                              <div className="ipv-eyebrow mb-3 mt-6 flex items-center gap-1">Payment</div>
                              <div className="space-y-1 text-gray-700">
                                <div><b>Status:</b> <StatusBadge status={o.payment_status} /></div>
                                <div><b>Method:</b> {o.payment_method || (o.channel === "pos" ? "card_pos_terminal" : "card")}</div>
                                {o.square_payment_id && <div><b>Square ID:</b> <code className="text-[10px]">{o.square_payment_id}</code></div>}
                                {!o.square_payment_id && o.payment_status === "pending" && (
                                  <div className="text-amber-700 text-xs italic">No Square charge — the payment was never completed.</div>
                                )}
                                {o.receipt_url && <div><a className="text-[#0A1C40] hover:underline" href={o.receipt_url} target="_blank" rel="noreferrer">Square receipt →</a></div>}
                                {o.channel === "pos" && o.served_by_name && (
                                  <div className="text-xs text-gray-500 mt-2">In-store sale served by <b>{o.served_by_name}</b></div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
